@if (isEventConfirmed) {
  <div class="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
    <div class="flex items-center gap-2">
      <lucide-icon [name]="LockIcon" [size]="16" class="text-amber-600 dark:text-amber-400"></lucide-icon>
      <h4 class="text-sm font-medium text-amber-800 dark:text-amber-200">Event Confirmed - Products Locked</h4>
    </div>
    <p class="text-xs text-amber-700 dark:text-amber-300 mt-1">
      Existing products cannot be edited or deleted. New products can be added and will be locked at current admin pricing.
    </p>
  </div>
}

<!-- VG-408: £500 Offer Banner -->
@if (financialSummary) {
  @if (isOfferEligible) {
    <div class="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
      <div class="flex items-center justify-between">
        <div class="flex-1">
          <h4 class="text-base font-semibold text-green-900 dark:text-green-100">£500 Offer Unlocked!</h4>
          <p class="text-sm text-green-700 dark:text-green-300 mt-1">
            Qualifying total: {{ qualifyingTotal | currency:'GBP' }}
          </p>
            @if (offerDiscountApplied > 0) {
              <p class="text-sm font-medium mt-1" [ngClass]="{
                'text-green-700 dark:text-green-300': offerProductsTotal <= 150,
                'text-amber-700 dark:text-amber-300': offerProductsTotal > 150
              }">
                Discount Used: {{ offerDiscountApplied | currency:'GBP' }} of £150.00
              </p>
              @if (offerProductsTotal > 150) {
                <p class="text-xs text-amber-700 dark:text-amber-300 mt-1 flex items-center gap-1">
                  <lucide-icon [name]="AlertTriangleIcon" [size]="14" class="shrink-0"></lucide-icon>
                  <span>Selected {{ offerProductsTotal | currency:'GBP' }} worth of products. Balance of {{ (offerProductsTotal - 150) | currency:'GBP' }} will be added to invoice.</span>
                </p>
              }
            }
        </div>
        <app-button
          label="Select £150 Offer Products"
          style="primary"
          (click)="showOfferSelection()"
        ></app-button>
      </div>
    </div>
  } @else if (qualifyingTotal > 0) {
    <div class="mb-4 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-500 rounded-lg">
      <div class="px-4 py-3 flex items-start gap-3">
        <lucide-icon name="AlertCircle" [size]="18" class="mt-0.5 text-indigo-600 dark:text-indigo-300"></lucide-icon>
        <div class="flex-1">
          <p class="text-sm font-semibold text-indigo-800 dark:text-indigo-100">
            Unlock £150 discount – spend {{ (500 - qualifyingTotal) | currency:'GBP' }} more on qualifying products
          </p>
          <p class="text-xs text-indigo-700 dark:text-indigo-200 mt-1">
            Add qualifying products until the progress bar hits £500 to unlock the offer.
          </p>
        </div>
      </div>
      <div class="px-4 pb-4">
        <div class="h-2 rounded-full bg-indigo-100 dark:bg-indigo-950/40 overflow-hidden">
          <div class="h-full bg-indigo-500 dark:bg-indigo-400 transition-all duration-300" [style.width]="progressToOffer"></div>
        </div>
      </div>
    </div>
  }
}

@if (productItemsPage) {
  <app-data-table
    (route)="this.handleRoute($event)"
    [title]="'Event Product Items'"
    [dataPage]="productItemsPage"
    [rowActions]="rowActions"
    [reorderable]="canReorderProducts"
    (reorder)="onProductsReordered($event)"
    [columns]="tableColumns">
    <div header class="flex flex-row w-full items-center justify-between gap-3">
      <div class="w-60">
        <app-select
          [height]="'12'"
          [multiple]="true"
          [control]="categoriesControl"
          [choices]="categories"
          [placeholder]="'Filter by categories'"></app-select>
      </div>
      <app-text-input [control]="searchControl" [placeholder]="'Search products'"></app-text-input>
      <button
        type="button"
        class="h-12 px-4 rounded-lg border text-sm font-medium transition-colors"
        [ngClass]="unlinkedOnlyControl.value
          ? 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-white/[0.03]'"
        (click)="unlinkedOnlyControl.setValue(!unlinkedOnlyControl.value)">
        {{ unlinkedOnlyControl.value ? 'Showing Unlinked Only' : 'Filter Unlinked' }}
      </button>
      <app-button
        [label]="'Add product to event'"
        [style]="'primary'"
        [icon]="PlusIcon"
        [title]="isEventConfirmed ? 'New products will be locked at current admin price' : 'Add product to event'"
        (click)="openRightSidebar('product-item-add')"></app-button>
      @if (isAdmin) {
        <app-button
          [label]="'Add discount / adjustment'"
          [style]="'secondary'"
          [icon]="PlusIcon"
          [title]="'Admin only: add a discount or adjustment line to this event'"
          (click)="openRightSidebar('adjustment-add')"></app-button>
      }
    </div>
    @if (financialSummary) {
      <div footer class="mt-6 flex flex-col w-full items-end justify-end gap-3">
        @if (!isConfirmedStatus) {
          <div class="flex gap-3 items-end justify-end h-full">
            <div class="text-gray-500">Minimum spend for event date:</div>
            <div class="text-green-600">{{ minimumSpendDisplay | currency:'GBP' }}</div>
          </div>
          <div class="flex gap-3 items-end justify-end h-full"
               [ngClass]="{'text-red-600': financialSummary.minimum_spend_left > 0}"
          >
            <div>Amount required to reach minimum spend:</div>
            @if (financialSummary.minimum_spend_left > 0) {
              <div>{{ financialSummary.minimum_spend_left | currency:'GBP' }}</div>
            } @else {
              <div>{{ 0 | currency:'GBP' }}</div>
            }
          </div>
        }
        <div class="flex flex-col gap-1 items-end justify-end border-t border-gray-200 dark:border-gray-800 pt-3">
          @if (financialSummary.venue_hire_total > 0) {
            <div class="flex gap-3 items-center">
              <div class="text-gray-500">Venue Hire:</div>
              <div class="text-blue-600">{{ financialSummary.venue_hire_total | currency:'GBP' }}</div>
            </div>
          }
          @if (financialSummary.accommodation_total > 0) {
            <div class="flex gap-3 items-center">
              <div class="text-gray-500">Accommodation:</div>
              <div class="text-blue-600">{{ financialSummary.accommodation_total | currency:'GBP' }}</div>
            </div>
          }
          <div class="flex gap-3 items-center">
            <div class="text-gray-500">Summary of products:</div>
            <div class="text-green-600">{{ financialSummary.total_products_value | currency:'GBP' }}</div>
          </div>

          <!-- TVG-156: admin-only discount / adjustment lines -->
          @if (adjustments.length > 0) {
            <div class="flex flex-col gap-1 items-end border-t border-gray-200 dark:border-gray-800 pt-2 mt-2">
              @for (adjustment of adjustments; track adjustment.id) {
                <div class="flex gap-3 items-center">
                  <div class="text-gray-500">
                    {{ adjustment.description }}
                    <span class="text-xs text-gray-400">({{ adjustment.category }})</span>
                  </div>
                  <div [ngClass]="adjustment.amount < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600'">
                    {{ adjustment.amount | currency:'GBP' }}
                  </div>
                  @if (isAdmin) {
                    <button type="button"
                            class="text-gray-400 hover:text-red-600"
                            [title]="'Remove adjustment line'"
                            (click)="deleteAdjustmentLine(adjustment)">
                      <lucide-icon [name]="TrashIcon" [size]="16"></lucide-icon>
                    </button>
                  }
                </div>
              }
              <div class="flex gap-3 items-center font-medium">
                <div class="text-gray-500">Adjustments total:</div>
                <div [ngClass]="financialSummary.adjustments_total < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600'">
                  {{ financialSummary.adjustments_total | currency:'GBP' }}
                </div>
              </div>
            </div>
          }

          <!-- VG-408: £150 Offer Discount Display -->
          @if (offerDiscountApplied > 0) {
            <div class="flex gap-3 items-center border-t border-green-200 dark:border-green-800 pt-2 mt-2">
              <div class="text-green-700 dark:text-green-400">£150 Offer Discount:</div>
              <div class="text-green-600 dark:text-green-500 font-semibold">-{{ offerDiscountApplied | currency:'GBP' }}</div>
            </div>
          }

          @if (isConfirmedStatus && event.deposit && event.deposit > 0) {
            <div class="flex gap-3 items-center border-t border-gray-200 dark:border-gray-800 pt-2 mt-2">
              <div class="text-gray-500">Refundable Deposit (included in grand total):</div>
              <div class="text-blue-600 font-semibold">{{ event.deposit | currency:'GBP' }}</div>
            </div>
          }

          <div class="flex gap-3 items-center font-semibold text-lg border-t border-gray-300 dark:border-gray-700 pt-2 mt-2">
            <div class="text-gray-700 dark:text-gray-300">Grand Total:</div>
            <div class="text-green-600 dark:text-green-500">{{ financialSummary.grand_total | currency:'GBP' }}</div>
          </div>
        </div>
      </div>
    }
  </app-data-table>
}

@if (financialSummary && isConfirmedStatus) {
  <div class="mt-8 rounded-2xl border border-gray-200 bg-gray-50 p-6 dark:border-gray-800 dark:bg-gray-900/50">
    <h3 class="mb-4 text-base font-semibold text-gray-800 dark:text-white/90">
      Contractual Checks
    </h3>

    <div class="space-y-4">
      <!-- Grand Total & Contractual Spend -->
      <div class="space-y-2">
        <div class="flex items-center justify-between">
          <span class="text-sm text-gray-600 dark:text-gray-400">Grand total & contractual spend</span>
          <div class="flex items-center gap-3">
            <span class="text-sm font-medium">{{ financialSummary.grand_total | currency:'GBP' }}</span>
            <span class="text-xs text-gray-400">|</span>
            <span class="text-sm font-medium">{{ financialSummary.contractual_spend | currency:'GBP' }}</span>
          </div>
        </div>
        <div class="flex items-center justify-between pl-4">
          <span class="text-sm text-gray-600 dark:text-gray-400">Amount Below contractual spend</span>
          <span class="text-sm font-semibold"
                [ngClass]="{'text-red-600 dark:text-red-400': financialSummary.contractual_spend_left > 0, 'text-green-600 dark:text-green-400': financialSummary.contractual_spend_left === 0}">
            {{ financialSummary.contractual_spend_left | currency:'GBP' }}
          </span>
        </div>
      </div>

      <!-- Extras Total & Contractual Spend -->
      <div class="space-y-2 border-t border-gray-200 pt-4 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <span class="text-sm text-gray-600 dark:text-gray-400">Extras Total & contractual spend</span>
          <div class="flex items-center gap-3">
            <span class="text-sm font-medium">{{ financialSummary.total_extras_value | currency:'GBP' }}</span>
            <span class="text-xs text-gray-400">|</span>
            <span class="text-sm font-medium">{{ financialSummary.extras_contractual_spend | currency:'GBP' }}</span>
          </div>
        </div>
        <div class="flex items-center justify-between pl-4">
          <span class="text-sm text-gray-600 dark:text-gray-400">Amount Below extras contractual spend</span>
          <span class="text-sm font-semibold"
                [ngClass]="{'text-red-600 dark:text-red-400': financialSummary.extras_contractual_spend_left > 0, 'text-green-600 dark:text-green-400': financialSummary.extras_contractual_spend_left === 0}">
            {{ financialSummary.extras_contractual_spend_left | currency:'GBP' }}
          </span>
        </div>
      </div>

      <!-- DJ & Entertainment Total & Contractual Spend -->
      <div class="space-y-2 border-t border-gray-200 pt-4 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <span class="text-sm text-gray-600 dark:text-gray-400">DJ & Entertainment total & contractual spend</span>
          <div class="flex items-center gap-3">
            <span class="text-sm font-medium">{{ financialSummary.total_dj_value | currency:'GBP' }}</span>
            <span class="text-xs text-gray-400">|</span>
            <span class="text-sm font-medium">{{ financialSummary.dj_contractual_spend | currency:'GBP' }}</span>
          </div>
        </div>
        <div class="flex items-center justify-between pl-4">
          <span class="text-sm text-gray-600 dark:text-gray-400">Amount Below DJ & Entertainment contractual spend</span>
          <span class="text-sm font-semibold"
                [ngClass]="{'text-red-600 dark:text-red-400': financialSummary.dj_contractual_spend_left > 0, 'text-green-600 dark:text-green-400': financialSummary.dj_contractual_spend_left === 0}">
            {{ financialSummary.dj_contractual_spend_left | currency:'GBP' }}
          </span>
        </div>
      </div>

      <!-- Minimum Spend -->
      <div class="space-y-2 border-t border-gray-200 pt-4 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span class="text-sm text-gray-600 dark:text-gray-400">Contractual minimum spend for event date</span>
            @if (isContractualMinimumMissing) {
              <div class="relative group">
                <lucide-icon
                  [name]="AlertCircleIcon"
                  [size]="16"
                  class="text-amber-600 dark:text-amber-400 cursor-help"
                ></lucide-icon>
                <div
                  class="absolute z-10 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-200
                         bottom-full left-1/2 transform -translate-x-1/2 mb-2
                         bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg shadow-lg p-3
                         min-w-[240px] max-w-[320px]"
                >
                  <div class="font-semibold mb-1">Using live minimum spend (no confirmation snapshot found).</div>
                  <div>Confirm the event to capture the snapshot so this locks; if already confirmed, re-confirm or run the contractual backfill.</div>
                  <div class="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                    <div class="border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                  </div>
                </div>
              </div>
            }
          </div>
          <span class="text-sm font-medium text-green-600 dark:text-green-400">{{ contractualMinimumSpend | currency:'GBP' }}</span>
        </div>
        <div class="flex items-center justify-between pl-4">
          <span class="text-sm text-gray-600 dark:text-gray-400">Amount Below minimum spend</span>
          <span class="text-sm font-semibold"
                [ngClass]="{'text-red-600 dark:text-red-400': contractualMinimumSpendLeft > 0, 'text-green-600 dark:text-green-400': contractualMinimumSpendLeft <= 0}">
            @if (contractualMinimumSpendLeft > 0) {
              {{ contractualMinimumSpendLeft | currency:'GBP' }}
            } @else {
              {{ 0 | currency:'GBP' }}
            }
          </span>
        </div>
      </div>
    </div>
  </div>
}

<!-- TVG-128 / TVG-206: the entire Event Payments section is gated behind the
     `payment-summary-box-staff` toggle. When off, the heading, Add payment
     button, breakdown link, payment table and totals box are all hidden. -->
<div class="mt-10">
  @if (paymentsPage && paymentSummaryBoxEnabled) {
    <app-data-table
      (route)="this.handleRoute($event)"
      [title]="'Event Payments'"
      [dataPage]="paymentsPage"
      [paginate]="false"
      [noDataPlaceholder]="false"
      [columns]="paymentsTableColumns"
      [rowActions]="paymentsTableRowActions">
      >
      <div header class="flex flex-row w-full items-start justify-between gap-3">
        <div class="flex flex-col gap-1">
          <div>
            @if (isAdmin) {
              <app-button [label]="'Add payment'" [style]="'primary'" [icon]="PlusIcon" (click)="openRightSidebar('payment-add')"></app-button>
            } @else {
              <button
                class="h-12 text-theme-sm shadow-theme-xs inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 font-medium text-gray-400 cursor-not-allowed"
                disabled
                title="Only admins can add or edit payments">
                <lucide-icon [name]="PlusIcon"></lucide-icon>
                Add payment
              </button>
            }
          </div>
          <div>
            <button
              class="text-xs text-gray-500 hover:text-primary-500 dark:text-gray-400 dark:hover:text-primary-400 underline-offset-4 hover:underline"
              [routerLink]="['/events', event.id, 'financial-breakdown']">
              View detailed breakdown
            </button>
          </div>
        </div>
        <!-- Auto-sync payments toggle hidden
        <div class="flex items-center gap-2">
          <div class="flex items-center gap-2" title="When enabled, system-generated payments (like deposits and final payments) will automatically recalculate their amounts when products are added, removed, or modified. Manual payments remain unchanged.">
            <div class="flex items-center gap-2">
              <label (click)="paymentSyncControl.setValue(!paymentSyncControl.value)" class="text-sm text-gray-600 dark:text-gray-400 cursor-pointer select-none">Auto-sync payments</label>
              <app-checkbox
                [control]="paymentSyncControl"
                [label]="''">
              </app-checkbox>
            </div>
            <lucide-icon [name]="HelpCircleIcon" [size]="16" class="text-gray-400 cursor-help"></lucide-icon>
          </div>
        </div>
        -->
      </div>
      @if (financialSummary) {
        <div footer class="mt-6 flex flex-col w-full items-end justify-end gap-3">
          @if (financialSummary.unabsorbed_credit > 0 && event.category === 'Wedding') {
            <div class="flex gap-3 items-end justify-end h-full">
              <div class="flex items-center gap-2">
                <div class="text-gray-500">Unallocated Credit:</div>
                <lucide-icon [name]="HelpCircleIcon" [size]="16" class="text-gray-400 cursor-help"
                  title="Reservation fee exceeds current venue hire costs. This credit will be applied to future products added to the event."></lucide-icon>
              </div>
              <div class="text-emerald-600 font-semibold">{{ financialSummary.unabsorbed_credit | currency:'GBP' }}</div>
            </div>
          }
          <div class="flex gap-3 items-end justify-end h-full">
            <div class="text-gray-500">Total Payments Due:</div>
            <div class="text-blue-600">{{ financialSummary.total_payments_due | currency:'GBP' }}</div>
          </div>
          <div class="flex gap-3 items-end justify-end h-full">
            <div class="text-gray-500">Total Payments Paid:</div>
            <div class="text-green-600">{{ financialSummary.total_payments_paid | currency:'GBP' }}</div>
          </div>
          @if (financialSummary.total_payments_outstanding > 0) {
            <div class="flex gap-3 items-end justify-end h-full">
              <div class="text-gray-500">Outstanding Payments:</div>
              <div class="text-red-600">
                {{ financialSummary.total_payments_outstanding | currency:'GBP' }}
              </div>
            </div>
          } @else if (financialSummary.total_payments_outstanding < 0) {
            <div class="flex gap-3 items-end justify-end h-full">
              <div class="text-gray-500">Credit (Overpaid):</div>
              <div class="text-green-600 font-semibold">
                {{ (-financialSummary.total_payments_outstanding) | currency:'GBP' }}
              </div>
            </div>
          } @else {
            <div class="flex gap-3 items-end justify-end h-full">
              <div class="text-gray-500">Balance:</div>
              <div class="text-green-600">
                {{ 0 | currency:'GBP' }}
              </div>
            </div>
          }
          @if (financialSummary.excess_credits_available && financialSummary.excess_credits_available > 0) {
            <div class="flex gap-3 items-end justify-end h-full border-t border-gray-200 dark:border-gray-800 pt-3">
              <div class="flex items-center gap-2">
                <div class="text-gray-500">Excess Credits Available:</div>
                <lucide-icon [name]="HelpCircleIcon" [size]="16" class="text-gray-400 cursor-help"
                  title="Credits that exceed the final payment amount. Available for refund or to cover future charges."></lucide-icon>
              </div>
              <div class="text-purple-600 font-semibold">
                {{ financialSummary.excess_credits_available | currency:'GBP' }}
              </div>
            </div>
          }
          @if (financialSummary.overdue_payments > 0) {
            <div class="flex gap-3 items-end justify-end h-full border-t border-gray-200 dark:border-gray-800 pt-3">
              <div class="text-gray-500">Overdue Payments:</div>
              <div class="text-red-600 font-semibold">{{ financialSummary.overdue_payments | currency:'GBP' }}</div>
            </div>
          }
        </div>
      }
    </app-data-table>
  }
</div>

<app-right-sidebar [isSidebarOpen]="isRightSidebarOpen" [customWidth]="sidebarCustomWidth" (closed)="closeRightSidebar()">
  @if (rightSideBarContent == 'product-item-add') {
    <app-event-product-item-form
      #productItemForm
      [event]="event"
      [showTimelineSelector]="true"
      [timelineItemChoices]="timelineItemChoices"
      (itemCreated)="productItemCreated($event)"
    ></app-event-product-item-form>
  }
  @if (rightSideBarContent == 'product-item-edit' && editProductItem) {
    <app-event-product-item-edit-form
      #productItemEditForm
      [event]="event"
      [productItem]="editProductItem"
      [showTimelineSelector]="true"
      [timelineItemChoices]="timelineItemChoices"
      (itemUpdated)="productItemUpdated($event)"
      (formCancelled)="closeRightSidebar()"
      [resetProcessingToken]="productEditResetToken"
    ></app-event-product-item-edit-form>
  }
  @if (rightSideBarContent == 'payment-add') {
    <app-event-payment-form
      [event]="event"
      (paymentCreated)="paymentItemCreated($event)"
    ></app-event-payment-form>
  }
  @if (rightSideBarContent == 'payment-edit' && editPayment) {
    <app-event-payment-edit-form
      [event]="event"
      [payment]="editPayment"
      (paymentUpdated)="paymentItemUpdated($event)"
      (formCancelled)="closeRightSidebar()"
    ></app-event-payment-edit-form>
  }
  @if (rightSideBarContent == 'adjustment-add') {
    <app-adjustment-line-form
      #adjustmentForm
      [processing]="savingAdjustment"
      (created)="adjustmentCreated($event)"
      (cancelled)="closeRightSidebar()"
    ></app-adjustment-line-form>
  }
  @if (rightSideBarContent == 'offer-selection') {
    <app-offer-products-selection
      [event]="event"
      [eligibleItems]="offerSelectionItems"
      [currentOfferItemIds]="currentOfferItemIds"
      [blockedItemIds]="blockedOfferItemIds"
      [processing]="isApplyingOffer"
      [errorMessage]="offerSelectionError"
      (offerApplied)="applyOfferSelection($event)"
      (cancelled)="closeRightSidebar()"
    ></app-offer-products-selection>
  }
</app-right-sidebar>

<app-modal
  [title]="'Link Product to Itinerary'"
  [show]="!!linkProductItem"
  [closeButtonLabel]="'Cancel'"
  [confirmButtonLabel]="linkingTimeline ? 'Saving...' : 'Save Link'"
  [processing]="linkingTimeline"
  [confirmDisabled]="timelineItems.length === 0 && !linkProductItem?.event_timeline_item_id && !linkProductItem?.event_timeline_item?.id"
  [compact]="true"
  (onClose)="closeTimelineLinkModal()"
  (onConfirm)="confirmTimelineLink()"
>
  @if (linkProductItem) {
    <div class="space-y-3">
      <p class="text-sm text-gray-700 dark:text-gray-300">
        {{ linkProductItem.product.name }}
      </p>
      @if (timelineItems.length > 0) {
        <app-select
          [height]="'11'"
          [control]="linkTimelineItemControl"
          [choices]="timelineItemChoices"
          [label]="'Itinerary Item'"
          [placeholder]="'No itinerary item'"
          [clearable]="true">
        </app-select>
      } @else {
        <div class="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
          @if (linkProductItem.event_timeline_item_id || linkProductItem.event_timeline_item?.id) {
            No itinerary items exist for this event. Saving will remove the current link.
          } @else {
            No itinerary items exist for this event yet.
          }
        </div>
      }
    </div>
  }
</app-modal>

<app-modal
  [title]="'Delete Product'"
  [content]="'Are you sure you want to delete this product?'"
  [show]="!!deleteProductItem"
  [closeButtonLabel]="'Cancel'"
  [confirmButtonLabel]="deletingProduct ? 'Deleting...' : 'Yes, Proceed'"
  [processing]="deletingProduct"
  (onClose)="deleteProductItem = undefined"
  (onConfirm)="confirmDelete()"
></app-modal>

<app-modal
  [title]="'Delete Payment'"
  [content]="'Are you sure you want to delete this payment record?'"
  [show]="!!deletePayment"
  [closeButtonLabel]="'Cancel'"
  [confirmButtonLabel]="'Yes, Proceed'"
  (onClose)="deletePayment = undefined"
  (onConfirm)="confirmDeletePayment()"
></app-modal>

<app-modal
  [title]="djEntertainmentWarningTitle"
  [show]="showDjEntertainmentWarning"
  [closeButtonLabel]="'OK'"
  [confirm]="false"
  (onClose)="showDjEntertainmentWarning = false"
>
  <div class="space-y-4">
    <div class="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900 shadow-sm dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-100">
      <p class="text-sm font-semibold">Action required</p>
      <p class="text-sm mt-2">
        DJ & Entertainment products cannot be added to the First Payment because the event is less than 9 months away.
      </p>
    </div>

    <div class="rounded-xl border border-gray-200 bg-gray-50 p-4 text-gray-700 shadow-sm dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300">
      <p class="text-sm">
        Please ensure this amount is added manually to the Event Payments section.
      </p>
    </div>

    <div class="grid gap-3 sm:grid-cols-2">
      <div class="rounded-xl border border-blue-200 bg-blue-50 p-4 text-blue-900 shadow-sm dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-100">
        <p class="text-sm font-semibold">Event Managers</p>
        <p class="text-sm mt-2">
          Please contact an admin to update the Event Payments accordingly.
        </p>
      </div>
      <div class="rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-indigo-900 shadow-sm dark:border-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-100">
        <p class="text-sm font-semibold">Admins</p>
        <p class="text-sm mt-2">
          Please remember to add this payment manually so the Event Payments section accurately reflects this product.
        </p>
      </div>
    </div>
  </div>
</app-modal>

<!-- VG-408: Warning modal for qualifying product removal -->
<app-modal
  [title]="'Warning: Offer Qualification'"
  [show]="showQualifyingProductWarning"
  [closeButtonLabel]="'Cancel'"
  [confirmButtonLabel]="'Yes, Remove Product & Offer'"
  (onClose)="cancelQualifyingProductDelete()"
  (onConfirm)="confirmQualifyingProductDelete()"
>
  <div class="space-y-3">
    <p class="text-sm text-gray-700 dark:text-gray-300">
      Removing this product will cause the qualifying total to drop below £500.
    </p>
    <div class="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
      <p class="text-sm text-amber-800 dark:text-amber-200 font-medium">
        This will automatically remove all £150 offer products and their discounts.
      </p>
    </div>
    <p class="text-sm text-gray-700 dark:text-gray-300">
      Are you sure you want to proceed?
    </p>
  </div>
</app-modal>
